import React, { useState, useCallback } from 'react';
import UnifiedMathInput from '../common/UnifiedMathInput';
import ImageInputButton from './ImageInputButton';

interface ChatInputAreaProps {
  connected: boolean;
  isTyping: boolean;
  isUploading: boolean;
  loadingHistory: boolean;
  isTopicCompleted: boolean;
  currentTopic: any;
  selectedModel: 'gemini' | 'deepseek';
  onSendMessage: (message: string) => void;
  onImageSelected?: (file: File) => void;
}

/**
 * 聊天输入区域组件
 * 独立的输入组件，状态变化不会影响消息列表渲染
 */
export const ChatInputArea: React.FC<ChatInputAreaProps> = React.memo(({
  connected,
  isTyping,
  isUploading,
  loadingHistory,
  isTopicCompleted,
  currentTopic,
  selectedModel,
  onSendMessage,
  onImageSelected
}) => {
  const [inputText, setInputText] = useState('');
  // 移除inputRef，因为UnifiedMathInput不支持ref

  // 处理发送消息
  const handleSend = useCallback(() => {
    if (inputText.trim() && connected && !isTyping && !isUploading && !loadingHistory && !isTopicCompleted) {
      onSendMessage(inputText.trim());
      setInputText('');
    }
  }, [inputText, connected, isTyping, isUploading, loadingHistory, isTopicCompleted, onSendMessage]);

  // 处理键盘事件
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // 输入框变化处理
  const handleInputChange = useCallback((value: string) => {
    setInputText(value);
  }, []);

  // 判断是否可以发送
  const canSend = connected && 
                  inputText.trim() && 
                  !isTyping && 
                  !isUploading && 
                  !loadingHistory && 
                  !isTopicCompleted;

  // 判断输入框是否应该被禁用
  const isInputDisabled = !connected || 
                          isTyping || 
                          isUploading || 
                          loadingHistory || 
                          isTopicCompleted;

  // 判断是否显示图片输入按钮（仅Gemini模型且有活跃话题时显示）
  const showImageButton = selectedModel === 'gemini' && 
                          currentTopic && 
                          !isTopicCompleted &&
                          onImageSelected !== undefined;

  // 处理图片选择
  const handleImageSelected = useCallback((file: File) => {
    if (onImageSelected) {
      onImageSelected(file);
    }
  }, [onImageSelected]);

  return (
    <>
      <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="flex items-end space-x-3">
        <div className="flex-1">
          <UnifiedMathInput
            value={inputText}
            onChange={handleInputChange}
            placeholder={
              !connected ? "连接中..." :
              isTopicCompleted ? "话题已完成" :
              isTyping ? "AI正在回复..." :
              isUploading ? "上传中..." :
              loadingHistory ? "加载历史..." :
              "输入消息..."
            }
            onKeyPress={handleKeyPress}
            disabled={isInputDisabled}
            className="w-full"
            rows={1}
          />
        </div>

        <button
          onClick={handleSend}
          disabled={!canSend}
          className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          {isTyping || isUploading || loadingHistory ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>

      {/* 输入提示 */}
      {!connected && (
        <div className="mt-2 text-xs text-red-500 dark:text-red-400">
          连接已断开，请刷新页面重试
        </div>
      )}
      
      {isTopicCompleted && (
        <div className="mt-2 text-xs text-green-600 dark:text-green-400">
          🎉 话题已完成！点击"新问题"开始新的对话
        </div>
      )}
      
      {!currentTopic && connected && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          请在问题输入区提交问题开始对话
        </div>
      )}
    </div>

    {/* 图片输入浮动按钮 - 仅Gemini模型时显示 */}
    {showImageButton && (
      <ImageInputButton
        onImageSelected={handleImageSelected}
        disabled={isInputDisabled}
      />
    )}
  </>
  );
});

ChatInputArea.displayName = 'ChatInputArea';

export default ChatInputArea;